import React from 'react';
import { connect } from '../../redux/store';
import { compose, lifecycle } from 'recompose';
import { Link } from '@reach/router';
import { ErrorMessage, LoadingMessage, C } from '@kineticdata/bundle-common';
import { RequestShowConfirmationContainer } from './RequestShowConfirmation';
import { RequestActivityList } from './RequestActivityList';
import { RequestMetaWrapper } from './RequestMetaWrapper';
import { CommentsList } from '../shared/CommentsList';
import { IssuesList } from '../shared/IssuesList';
import { RunsList } from '../shared/RunsList';
import { tabLabels } from '../../redux/modules/requests';
import { actions } from '../../redux/modules/submission';
import { getStatus, getRequestStatusColor, isActiveClass } from '../../utils';
import { PageTitle } from '../shared/PageTitle';
import { CoreForm, I18n } from '@kineticdata/react';
import { getIn } from 'immutable';

const RequestComponent = ({ submission, error, pending, ...props }) => {
  const progress =
    submission && !C.HIDE_PROGRESS_STATUSES.includes(getStatus(submission))
      ? parseInt(submission.values[C.FIELD_PROGRESS], 10)
      : null;

  return (
    <div className="page-container">
      <div className="page-panel max-width-1600">
        <RequestMetaWrapper submission={submission} navigate={props.navigate}>
          {meta => (
            <PageTitle
              className="page-title-heading--sm"
              parts={[submission && submission.label, 'My Requests']}
              breadcrumbs={[
                {
                  label: (
                    <span>
                      <span className="fa fa-fw fa-md fa-arrow-left text-muted" />
                      <I18n>{props.typeLabel}</I18n>
                    </span>
                  ),
                  to: '../../..',
                },
              ]}
              title={!error && submission && submission.label}
              suptitle={
                !error &&
                submission && (
                  <>
                    <strong>
                      <I18n>{submission.form.name}</I18n>
                    </strong>
                    {' \u00b7 '}
                    {meta.showIdList().join(' \u00b7 ')}
                  </>
                )
              }
              subtitle={
                !error && submission && submission.values[C.FIELD_SUMMARY]
              }
              actions={
                !error &&
                submission &&
                [
                  {
                    label: 'Add Comment',
                    onClick: meta.handleAddComment(),
                    menu: true,
                  },
                  {
                    label: 'Duplicate',
                    onClick: meta.handleClone(),
                    menu: true,
                  },
                  {
                    label: meta.trackedCloneLabel,
                    onClick: meta.handleCloneWithTracking(),
                    menu: true,
                  },
                  {
                    label: 'Cancel',
                    onClick: meta.handleCancel({
                      success: () => props.navigate('../../..'),
                    }),
                    menu: true,
                  },
                  {
                    label: 'Report Success/Failure',
                    onClick: meta.handleConfirmSuccess(),
                    menu: true,
                  },
                  {
                    label: 'Escalate',
                    onClick: meta.handleEscalate({
                      // If the user is on the review tab, redirect to the
                      // activity tab because opening the escalation modal over
                      // another form causes issues with ambiguous jquery
                      // selectors since they both load the same subforms.
                      init:
                        props.mode === 'review'
                          ? () => props.navigate('../activity')
                          : undefined,
                    }),
                    menu: true,
                  },
                  {
                    label: 'PVG Override',
                    onClick: meta.handlePVGOverride(),
                    menu: true,
                  },
                  {
                    label: 'Add Issue',
                    onClick: meta.handleAddIssue(),
                    menu: true,
                  },
                ].filter(a => !!a.onClick)
              }
              meta={
                !error &&
                submission &&
                [
                  {
                    value: (
                      <span
                        className={`badge badge-${getRequestStatusColor(
                          submission,
                        )} badge-stylized text-muted`}
                      >
                        <I18n>{getStatus(submission)}</I18n>
                      </span>
                    ),
                  },
                  {
                    label:
                      submission && submission.submittedAt
                        ? 'Submitted'
                        : 'Created',
                    value: meta.showDisplayDate(),
                  },
                  {
                    label: 'Service Level Target',
                    value: meta.showSLT({ active: true, total: true }),
                  },
                  { label: 'Closed', value: meta.showClosedDate() },
                  { label: 'Tracking', value: meta.showTracking() },
                  {
                    label: 'Servers',
                    value: meta.showServerNames({ modal: true }),
                  },
                  { value: meta.showIssues() },
                ].filter(m => !!m.value)
              }
            />
          )}
        </RequestMetaWrapper>

        {error && (
          <ErrorMessage
            title="Failed to load request"
            message={error.message}
          />
        )}
        {!error && !submission && <LoadingMessage />}
        {!error &&
          submission && (
            <>
              {!!progress &&
                progress < 100 && (
                  <div className="submission-progress mb-5">
                    <div
                      className="submission-progress--bar"
                      style={{ width: `${progress}%` }}
                      title={`${progress}%`}
                      aria-label="Request Progress"
                    >
                      <div className="submission-progress--value">
                        {progress}%
                      </div>
                    </div>
                  </div>
                )}

              {props.mode === 'confirmation' && (
                <div className="alert alert-primary alert-bar mb-5">
                  <RequestShowConfirmationContainer submission={submission} />
                </div>
              )}

              <div className="bg-light p-4 mb-5 rounded">
                <div className="submission-tabs p-0">
                  <ul className="nav nav-tabs" role="tablist">
                    <li role="tab" className="nav-item">
                      <Link
                        to="../activity"
                        getProps={isActiveClass('nav-link')}
                        replace={props.mode === 'confirmation'}
                      >
                        <I18n>Activity</I18n>
                      </Link>
                    </li>

                    <li role="tab" className="nav-item">
                      <Link
                        to="../review"
                        getProps={isActiveClass('nav-link')}
                        replace={props.mode === 'confirmation'}
                      >
                        <I18n>Review Request</I18n>
                      </Link>
                    </li>

                    <li role="tab" className="nav-item">
                      <Link
                        to="../issues"
                        getProps={isActiveClass(
                          `nav-link ${
                            props.hasUnresolvedIssues ? 'text-danger' : ''
                          }`,
                        )}
                        replace={props.mode === 'confirmation'}
                      >
                        <I18n>Issues</I18n>
                        {props.hasUnresolvedIssues && (
                          <span className="large-asterisk text-danger"> *</span>
                        )}
                        {props.issuesCount ? (
                          <span
                            className={`badge badge-pill badge-${
                              props.hasUnresolvedIssues ? 'danger' : 'subtle'
                            }`}
                          >
                            {' '}
                            {props.issuesCount}
                          </span>
                        ) : null}
                      </Link>
                    </li>

                    <li role="tab" className="nav-item">
                      <Link
                        to="../comments"
                        getProps={isActiveClass('nav-link')}
                        replace={props.mode === 'confirmation'}
                      >
                        <I18n>Comments</I18n>
                        {props.commentsCount ? (
                          <span className="badge badge-pill badge-subtle">
                            {' '}
                            {props.commentsCount}
                          </span>
                        ) : null}
                      </Link>
                    </li>

                    {props.profile.spaceAdmin && (
                      <li role="tab" className="nav-item">
                        <Link
                          to="../runs"
                          getProps={isActiveClass('nav-link')}
                          replace={props.mode === 'confirmation'}
                        >
                          <I18n>Runs</I18n>
                        </Link>
                      </li>
                    )}
                  </ul>

                  <RequestMetaWrapper
                    submission={submission}
                    navigate={props.navigate}
                  >
                    {meta => (
                      <div className="submission-tabs__content">
                        {props.mode === 'review' ? (
                          <I18n
                            context={`kapps.${props.kapp.slug}.forms.${
                              submission.form.slug
                            }`}
                          >
                            <CoreForm submission={submission.id} review />
                          </I18n>
                        ) : props.mode === 'issues' ? (
                          <IssuesList
                            issues={props.issues}
                            handleAddIssue={meta.handleAddIssue()}
                          />
                        ) : props.mode === 'comments' ? (
                          <CommentsList
                            submission={submission}
                            handleAddComment={meta.handleAddComment()}
                          />
                        ) : props.mode === 'runs' &&
                        props.profile.spaceAdmin ? (
                          <RunsList
                            id={submission.id}
                            spaceAdmin={props.profile.spaceAdmin}
                          />
                        ) : (
                          <RequestActivityList submission={submission} />
                        )}
                      </div>
                    )}
                  </RequestMetaWrapper>
                </div>
              </div>
            </>
          )}
      </div>
    </div>
  );
};

const issueStatusSortOrder = [
  'Pending',
  'Being Addressed',
  'Resolved',
  'Cancelled',
];
const sortIssuesByCreatedAt = (a, b) =>
  a.createdAt === b.createdAt ? 0 : a.createdAt < b.createdAt ? -1 : 1;
const sortIssuesByStatus = (a, b) =>
  a.values['Status'] === b.values['Status']
    ? sortIssuesByCreatedAt(a, b)
    : issueStatusSortOrder.indexOf(a.values['Status']) -
      issueStatusSortOrder.indexOf(b.values['Status']);

export const mapStateToProps = (state, props) => {
  const submission =
    state.submission.data && state.submission.data.id === props.submissionId
      ? state.submission.data
      : null;
  const issues = submission
    ? submission.descendants
        .filter(d => d.type === 'Issue')
        .sort(sortIssuesByStatus)
    : null;
  const issuesCount = issues ? issues.length : null;
  const commentsCount = getIn(
    submission || {},
    submission && submission.type === C.REQUEST_FORM_TYPE
      ? ['activities']
      : ['parent', 'activities'],
    [],
  ).filter(a => a.type === 'Comment').length;

  return {
    kapp: state.app.kapp,
    profile: state.app.profile,
    submission,
    error: state.submission.error,
    pending: state.submission.pending,
    typeLabel: `${(tabLabels.find(t => t.type === props.type) || {}).label ||
      ''} Requests`.trim(),
    mode: props.mode,
    appLocation: state.app.location,
    isSmallLayout: state.app.layoutSize === 'small',
    issues,
    issuesCount,
    hasUnresolvedIssues:
      !!issues &&
      issues.filter(i => i.coreState === C.CORE_STATE_DRAFT).length > 0,
    commentsCount,
  };
};

export const mapDispatchToProps = {
  fetchSubmission: actions.fetchSubmissionRequest,
  clearSubmission: actions.clearSubmissionRequest,
  startPoller: actions.startSubmissionPoller,
  stopPoller: actions.stopSubmissionPoller,
};

export const Request = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  lifecycle({
    componentDidMount() {
      this.props.fetchSubmission(this.props.submissionId);
      this.props.startPoller(this.props.submissionId);
    },
    componentDidUpdate(prevProps) {
      if (this.props.submissionId !== prevProps.submissionId) {
        this.props.stopPoller();
        this.props.fetchSubmission(this.props.submissionId);
        this.props.startPoller(this.props.submissionId);
      }
      // Check if submission is not a request and redirect appropriately
      if (
        this.props.submission &&
        (!prevProps.submission ||
          prevProps.submission.id !== this.props.submissionId) &&
        this.props.submission.form
      ) {
        // Redirect datastore forms
        if (this.props.submission.form.kapp.slug === C.KAPP_SLUG_DATASTORE) {
          if (this.props.submission.form.slug === C.FORM_SLUG_PORTFOLIO) {
            this.props.navigate(
              `${this.props.appLocation}/settings/catalog/portfolios/${
                this.props.submission.values[C.FIELD_PORTFOLIO_ID]
              }`,
              { replace: true },
            );
          } else if (this.props.submission.form.slug === C.FORM_SLUG_SERVICE) {
            this.props.navigate(
              `${this.props.appLocation}/settings/catalog/services/${
                this.props.submission.values[C.FIELD_SERVICE_ID]
              }`,
              { replace: true },
            );
          } else if (this.props.submission.form.slug === C.FORM_SLUG_APPENDIX) {
            this.props.navigate(
              `${this.props.appLocation}/settings/catalog/appendices/${
                this.props.submission.values[C.FIELD_APPENDIX_ID]
              }`,
              { replace: true },
            );
          } else {
            this.props.navigate(
              `/settings/datastore/${this.props.submission.form.slug}/${
                this.props.submission.id
              }`,
              { replace: true },
            );
          }
        }
        // Redirect approvals
        else if (this.props.submission.form.type === C.APPROVAL_FORM_TYPE) {
          this.props.navigate(
            `${this.props.appLocation}/approvals/approval/${
              this.props.submission.id
            }`,
            { replace: true },
          );
        }
        // Redirect actions
        else if (this.props.submission.form.type === C.ACTION_FORM_TYPE) {
          this.props.navigate(
            `${this.props.appLocation}/actions/action/${
              this.props.submission.id
            }`,
            { replace: true },
          );
        }
      }
    },
    componentWillUnmount() {
      this.props.clearSubmission();
      this.props.stopPoller();
    },
  }),
)(RequestComponent);
